import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const SystemSettingsPanel = ({ countries, currencies, onUpdateCountries, onUpdateCurrencies, loading }) => {
  const [activeTab, setActiveTab] = useState('countries');
  const [editMode, setEditMode] = useState(false);
  const [localCountries, setLocalCountries] = useState(countries);
  const [localCurrencies, setLocalCurrencies] = useState(currencies);
  const [newItem, setNewItem] = useState({ value: '', label: '' });
  const [saving, setSaving] = useState(false);

  const handleAddItem = () => {
    if (!newItem?.value || !newItem?.label) return;

    if (activeTab === 'countries') {
      setLocalCountries([...localCountries, newItem]);
    } else {
      setLocalCurrencies([...localCurrencies, newItem]);
    }
    setNewItem({ value: '', label: '' });
  };

  const handleRemoveItem = (index) => {
    if (activeTab === 'countries') {
      setLocalCountries(localCountries?.filter((_, i) => i !== index));
    } else {
      setLocalCurrencies(localCurrencies?.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    if (activeTab === 'countries') {
      await onUpdateCountries(localCountries);
    } else {
      await onUpdateCurrencies(localCurrencies);
    }
    setSaving(false);
    setEditMode(false);
  };

  const handleCancel = () => {
    setLocalCountries(countries);
    setLocalCurrencies(currencies);
    setNewItem({ value: '', label: '' });
    setEditMode(false);
  };

  const currentList = activeTab === 'countries' ? localCountries : localCurrencies;

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Globe" size={24} color="var(--color-primary)" />
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">System Settings</h3>
        </div>
        {!editMode && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditMode(true)}
            disabled={loading}
          >
            <Icon name="Edit" size={16} />
            <span className="ml-2">Edit</span>
          </Button>
        )}
      </div>
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('countries')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-smooth ${
            activeTab === 'countries' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Countries
        </button>
        <button
          onClick={() => setActiveTab('currencies')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-smooth ${
            activeTab === 'currencies' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Currencies
        </button>
      </div>
      {editMode && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg">
          <p className="text-sm font-caption font-semibold text-foreground mb-3">
            Add New {activeTab === 'countries' ? 'Country' : 'Currency'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <Input
              placeholder="Code (e.g., us, usd)"
              value={newItem?.value}
              onChange={(e) => setNewItem({ ...newItem, value: e?.target?.value?.toLowerCase() })}
            />
            <Input
              placeholder="Label (e.g., United States)"
              value={newItem?.label}
              onChange={(e) => setNewItem({ ...newItem, label: e?.target?.value })}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            disabled={!newItem?.value || !newItem?.label}
          >
            <Icon name="Plus" size={16} />
            <span className="ml-2">Add</span>
          </Button>
        </div>
      )}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {currentList?.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon 
                  name={activeTab === 'countries' ? 'MapPin' : 'DollarSign'} 
                  size={16} 
                  color="var(--color-primary)" 
                />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm">{item?.label}</p>
                <p className="text-xs text-muted-foreground uppercase">{item?.value}</p>
              </div>
            </div>
            {editMode && (
              <button
                onClick={() => handleRemoveItem(index)}
                className="p-2 rounded-lg hover:bg-error/10 transition-smooth"
              >
                <Icon name="Trash2" size={16} color="var(--color-error)" />
              </button>
            )}
          </div>
        ))}
      </div>
      {editMode && (
        <div className="flex items-center gap-3 mt-6">
          <Button
            variant="default"
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
          >
            {saving ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              <>
                <Icon name="Check" size={16} />
                <span className="ml-2">Save Changes</span>
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default SystemSettingsPanel;